import React from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  IconButton,
  Button,
  useColorModeValue,
  Switch,
} from '@chakra-ui/react';

import {
  EditIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
} from '@chakra-ui/icons';
import { FiGrid, FiList } from 'react-icons/fi';
import { motion } from 'framer-motion';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';

// 🎬 Motion Components
const MotionTr = motion(Tr);
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const DevelopmentTable = ({
  tableData,
  handleEditUser,
  handleDeleteUser,
  handlePageChange,
  handleRowClick,
  page,
  totalPages,
  loading,
  handleToggleActive,
  topActionButton,
}) => {
  // 🌈 UI Vars
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const rowHoverBg = useColorModeValue('gray.100', 'gray.700');
  const inactiveRowBg = useColorModeValue('red.50', 'red.900');
  const inactiveTextColor = useColorModeValue('inherit', 'white');

  const cardBg = useColorModeValue('white', 'gray.900');
  const borderCol = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('blue.50', 'gray.800');
  const headerText = useColorModeValue('blue.700', 'blue.200');
  const rowHover = useColorModeValue('blue.50', 'gray.700');
  const inactiveBg = useColorModeValue('red.50', 'red.900');
  const zebraBg = useColorModeValue('gray.50', 'gray.800');

  <style>
    {`
    .shimmer {
      background: linear-gradient(
        90deg,
        #e0e0e0 0%,
        #f5f5f5 50%,
        #e0e0e0 100%
      );
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `}
  </style>;

  // 🌟 View Mode State (Table / Card)
  const [viewMode, setViewMode] = React.useState('table');

  // 🌟 Doctor Card Component (with animations)
  const DoctorCardView = ({ data }) => {
    const cardBg2 = useColorModeValue('white', 'gray.800');

    return (
      <Flex wrap="wrap" gap={4}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} w="250px" h="140px" bg={cardBg2} shadow="md" p={4}>
                <Box height="20px" className="shimmer" borderRadius="6px" />
                <Box height="15px" className="shimmer" borderRadius="6px" />
                <Box height="15px" className="shimmer" borderRadius="6px" />
              </Box>
            ))
          : data.map((doc, i) => (
              <MotionBox
                key={doc._id}
                w="260px"
                bg={cardBg2}
                border="1px solid"
                borderColor="gray.300"
                shadow="md"
                borderRadius="lg"
                p={4}
                cursor="pointer"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                // transition={{ duration: 0.3, delay: i * 0.06 }}
                whileHover={{
                  scale: 1.03,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  transition: { type: 'spring', stiffness: 260 },
                }}
                onClick={() => handleRowClick(doc)}
              >
                <style>
                  {`
                    @keyframes pulse {
                      0% { transform: scale(1); opacity: 0.8; }
                      50% { transform: scale(1.05); opacity: 1; }
                      100% { transform: scale(1); opacity: 0.8; }
                    }
                  `}
                </style>

                <Text fontSize="lg" fontWeight="700">
                  {doc.name}
                </Text>

                <Text fontSize="sm" color="gray.500">
                  {doc.speciality || 'General Doctor'}
                </Text>

                <Text fontSize="sm" mt={1}>
                  Hospital: {doc.hospitalName || 'N/A'}
                </Text>
                <Text fontSize="sm">City: {doc.city || 'N/A'}</Text>
                <Text fontSize="sm">Phone: {doc.phone}</Text>

                <Flex justify="space-between" align="center" mt={3}>
                  <Box
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="700"
                    color={doc.active ? 'green.700' : 'red.700'}
                    bg={doc.active ? 'green.100' : 'red.100'}
                    animation="pulse 1.6s infinite ease-in-out"
                  >
                    {doc.active ? 'ACTIVE' : 'INACTIVE'}
                  </Box>
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Switch
                      isChecked={doc.active}
                      onChange={(e) =>
                        handleToggleActive(doc._id, e.target.checked)
                      }
                    />
                  </Box>
                </Flex>
              </MotionBox>
            ))}
      </Flex>
    );
  };

  // 🌟 Table Rows Rendering
  const renderTableRows = () => {
    if (loading) {
      return Array.from({ length: 6 }).map((_, index) => (
        <Tr key={index}>
          {Array.from({ length: 8 }).map((_, colIndex) => (
            <Td key={colIndex}>
              <Box height="20px" className="shimmer" borderRadius="6px" />
            </Td>
          ))}
        </Tr>
      ));
    }

    return tableData.map((doc, index) => (
      <MotionTr
        key={doc._id}
        cursor="pointer"
        onClick={() => handleRowClick(doc)}
        bg={
          doc.active === false
            ? inactiveBg
            : index % 2 === 0
            ? zebraBg
            : 'transparent'
        }
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.05 }}
        whileHover={{
          scale: 1.0006,
          backgroundColor: rowHover,
          transition: { duration: 0.2 },
        }}
      >
        <Td>{index + 1 + (page - 1) * 10}</Td>

        <Td width={'300px'}>
          <Text fontWeight="700">{doc.name}</Text>
          <Text fontSize="xs" color="gray.500">
            ID: {doc._id}
          </Text>
        </Td>

        <Td>
          <Text fontWeight="600" color="blue.600">
            {doc.specialty || 'General'}
          </Text>
        </Td>

        <Td>{doc.hospital || 'N/A'}</Td>
        <Td>{doc.city || 'N/A'}</Td>
        <Td>{doc.phone}</Td>

        <Td>
          <Box
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="700"
            color={doc.active ? 'green.700' : 'red.700'}
            bg={doc.active ? 'green.100' : 'red.100'}
            display="flex"
            justifyContent={'center'}
            alignItems={'center'}
          >
            {doc.active ? 'ACTIVE' : 'INACTIVE'}
          </Box>
        </Td>

        <Td>
          <Flex justify="center" alignItems="center" gap={4}>
            <IconButton
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              onClick={(e) => {
                e.stopPropagation();
                handleEditUser(doc);
              }}
            />
            <Box
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Switch
                isChecked={doc.active}
                onChange={(e) => handleToggleActive(doc._id, e.target.checked)}
              />
            </Box>
          </Flex>
        </Td>
      </MotionTr>
    ));
  };

  // 🌟 Pagination
  const renderPagination = () => (
    <MotionFlex
      justify="center"
      align="center"
      py={0.1}
      gap={3}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <IconButton
        icon={<ChevronLeftIcon />}
        size="sm"
        onClick={() => handlePageChange(page - 1)}
        isDisabled={page <= 1}
      />

      <Text fontWeight="600">
        Page {page} of {totalPages}
      </Text>

      <IconButton
        icon={<ChevronRightIcon />}
        size="sm"
        onClick={() => handlePageChange(page + 1)}
        isDisabled={page >= totalPages}
      />
    </MotionFlex>
  );

  return (
    <>
      {/* 🌟 VIEW MODE TOGGLE BUTTONS (Icon Only) */}
      <Flex justify="flex-end" mb={4} gap={2}>
        <IconButton
          aria-label="Table View"
          icon={<FiList size={18} />}
          size="md"
          variant={viewMode === 'table' ? 'solid' : 'outline'}
          borderRadius="full"
          colorScheme="blue"
          onClick={() => setViewMode('table')}
        />

        <IconButton
          aria-label="Card View"
          icon={<FiGrid size={18} />}
          size="md"
          variant={viewMode === 'card' ? 'solid' : 'outline'}
          borderRadius="full"
          colorScheme="blue"
          onClick={() => setViewMode('card')}
        />
      </Flex>
      {/* HEADER + RIGHT BUTTON */}
      <Flex align="center" mb={4}>
        <Text fontSize="22px" fontWeight="bold" color={textColor}>
          {topActionButton?.title}
        </Text>

        {topActionButton && (
          <Button
            leftIcon={topActionButton.icon || <AddIcon />}
            colorScheme="blue"
            onClick={topActionButton.onClick}
            ml="auto" // 👈 push extreme right
          >
            {topActionButton.label}
          </Button>
        )}
      </Flex>

      {/* 🌟 TABLE VIEW */}
      {viewMode === 'table' && (
        <Card
          w="100%"
          bg={cardBg}
          border="1px solid"
          borderColor={borderCol}
          shadow="lg"
          borderRadius="lg"
          p={2}
        >
          <Box overflowX="auto" maxHeight="calc(100vh - 280px)">
            <Table variant="simple" size="md">
              <Thead bg={headerBg} position="sticky" top={0} zIndex={10}>
                <Tr>
                  <Th color={headerText}>Sr. No.</Th>
                  <Th color={headerText}>Doctor</Th>
                  <Th color={headerText}>Specialty</Th>
                  <Th color={headerText}>Hospital</Th>
                  <Th color={headerText}>City</Th>
                  <Th color={headerText}>Phone</Th>
                  <Th color={headerText} textAlign="center">
                    Status
                  </Th>
                  <Th color={headerText} textAlign="center">
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>{renderTableRows()}</Tbody>
            </Table>
          </Box>

          {renderPagination()}
        </Card>
      )}

      {/* 🌟 CARD VIEW */}
      {viewMode === 'card' && <DoctorCardView data={tableData} />}
    </>
  );
};

DevelopmentTable.propTypes = {
  tableData: PropTypes.array.isRequired,
  handleEditUser: PropTypes.func.isRequired,
  handleDeleteUser: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  handleToggleActive: PropTypes.func.isRequired,
  topActionButton: PropTypes.shape({
    title: PropTypes.string,
    label: PropTypes.string.isRequired,
    icon: PropTypes.node,
    onClick: PropTypes.func.isRequired,
  }),
};

export default React.memo(DevelopmentTable);
